import * as d3S from 'd3-selection';

import * as actions from '../../../../../actions';
import { isDraggableByOpStatus } from '../../../../../common/flight/flight-check-status';
import {
  getTimeLabelForFlightArr,
  getTimeLabelForFlightDep,
} from '../../../../../common/flight/flight-labels';
import {
  MIN_RANGE_SELECTION,
  TRIP_PROFILE_MAX_LENGTH,
} from '../../../../../constants';
import {
  getDragPosition,
  getElementOffsetWithKoef,
  getLaneHeightKoef,
} from '../../../../../reducers/ui';
import { store as reduxStore } from '../../../../../root';
import { getICAOLabelFromAirport } from '../../../../../utils/airport';
import Flight from '../../../../../types/flight';
import {
  getEuclideanDistance,
  leftClickHandlerCreator,
  openFlightCenter,
  openOrderWindow,
  setDataTestEntityIdD3Elements,
} from '../../../../../utils';
import { hasPermission } from '../../../../../utils/check-permissions';
import { getAircraftIndexById } from '../../../../../selectors';

export const getFlightsD3creator = (
  d: d3S.Selection<any, any, any, any>
): d3S.Selection<any, any, any, any> => {
  const state = reduxStore.getState();
  const hasFCPermission = hasPermission(
    state,
    'AG-Timeline-Flight-Center-View'
  );
  const hasOWPermission = hasPermission(state, 'AG-Timeline-Order-Window-View');
  const { airportsById } = state.airports;
  const timeLabelId = state.ui.timeLabelDisplayMode;
  const { segmentsVisibility, positionMap } = state.ui;
  const { togglersState } = state.aircraft;
  const leftClickHandler = leftClickHandlerCreator(
    d => {
      if (
        getEuclideanDistance(reduxStore.getState().ui.timelineSelection) <
        MIN_RANGE_SELECTION
      ) {
        openFlightCenter((d as Flight).id);
      }
    },
    d =>
      getEuclideanDistance(reduxStore.getState().ui.timelineSelection) <
        MIN_RANGE_SELECTION &&
      openOrderWindow({ id: (d as Flight).flightOrderId }),
    hasFCPermission,
    hasOWPermission
  );

  const container = d
    .append('div')
    .classed('label-text', true)
    .style(
      'transform',
      d =>
        `translate(${d.x}px, ${d.y +
          d.ky *
            getElementOffsetWithKoef(
              segmentsVisibility,
              'flights',
              togglersState[d.aircraftId],
              positionMap
            ) -
          3}px)`
    )
    .classed('flights-text-label', true)
    .attr(
      'data-test-entity',
      setDataTestEntityIdD3Elements('flights-text-label')
    )
    .classed('wide', d => d.width > 80)
    .style('width', d => `${d.width}px`)
    .style(
      'height',
      d =>
        `${(positionMap.flights * d.ky) /
          getLaneHeightKoef(
            segmentsVisibility,
            togglersState[d.aircraftId],
            positionMap
          ) +
          3}px`
    )
    .style('border', (d: Flight & { isSelected: boolean }) =>
      d.isSelected ? '2px solid #1890FF' : 'none'
    )
    .attr(
      'draggable',
      (d: Flight) =>
        hasPermission(state, 'AG-Timeline-Fight-Move') &&
        isDraggableByOpStatus(d.legOperationalStatusId)
    )
    .on('dragstart', function(d: Flight) {
      const event = d3S.event as DragEvent;
      const { x, y } = getDragPosition(state.ui, event);
      const index = getAircraftIndexById(state, d.aircraftId);
      event.dataTransfer.setData('text', d.id.toString());
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setDragImage(new Image(10, 10), 0, 0);
      return reduxStore.dispatch(
        actions.userDragFlightsBegin({
          flightId: d.id,
          draggingPosition: {
            x,
            y,
            index,
          },
        })
      );
    })
    .on('mouseover', function(d) {
      const event = d3S.event as MouseEvent;
      d3S.select(this).style('cursor', 'pointer');
      if (!event.shiftKey) {
        reduxStore.dispatch(actions.userHoverFlight([d, this]));
      }
    })
    .on('contextmenu', function(d) {
      d3S.event.preventDefault();
      d3S.event.stopPropagation();
      reduxStore.dispatch(actions.userOpenFlightMenu([this, d]));
    })
    .on('mouseout', function() {
      const event = d3S.event as MouseEvent;
      const { left, right, top, bottom } = this.getBoundingClientRect();
      const { clientX, clientY } = event;
      const isOverFlight =
        clientX > left && clientX < right && clientY > top && clientY < bottom;
      if (!isOverFlight) {
        reduxStore.dispatch(actions.userCloseTooltip());
      }
    })
    .on('click', (d: Flight) => {
      const event = d3S.event as MouseEvent;
      if (event.shiftKey && isDraggableByOpStatus(d.legOperationalStatusId))
        return reduxStore.dispatch(
          actions.userToggleFlightSelection({ flightId: d.id })
        );
      if (!event.shiftKey) leftClickHandler(d);
    });
  container
    .append('div')
    .classed('flights-text-label-trip-profile', true)
    .attr(
      'data-test-entity',
      setDataTestEntityIdD3Elements('flights-text-label-trip-profile')
    )
    .style('display', (data: Flight) => (data.tripProfile ? 'block' : 'none'))
    .append('span')
    .text(
      (data: Flight) =>
        `${
          data.tripProfile
            ? data.tripProfile.slice(0, TRIP_PROFILE_MAX_LENGTH)
            : ''
        }`
    );
  container
    .append('div')
    .classed('flights-text-label-dep', true)
    .attr(
      'data-test-entity',
      setDataTestEntityIdD3Elements('flights-text-label-dep')
    )
    .style('width', (data: Flight) =>
      !!getTimeLabelForFlightDep(data, timeLabelId).length ? '60px' : '40px'
    )
    .style('left', (data: Flight) =>
      !!getTimeLabelForFlightDep(data, timeLabelId).length ? '-60px' : '-40px'
    )
    .append('span')
    .text(
      (data: Flight) =>
        getICAOLabelFromAirport(airportsById[data.departureAirportId]) +
        getTimeLabelForFlightDep(data, timeLabelId)
    );
  container
    .append('div')
    .classed('flights-text-label-arr', true)
    .attr(
      'data-test-entity',
      setDataTestEntityIdD3Elements('flights-text-label-arr')
    )
    .style('width', (data: Flight) =>
      !!getTimeLabelForFlightArr(data, timeLabelId).length ? '60px' : '40px'
    )
    .style('right', (data: Flight) =>
      !!getTimeLabelForFlightArr(data, timeLabelId).length ? '-60px' : '-40px'
    )
    .append('span')
    .text((data: Flight) => {
      return (
        getICAOLabelFromAirport(airportsById[data.arrivalAirportId]) +
        getTimeLabelForFlightArr(data, timeLabelId)
      );
    });
  return container;
};
